/**
 * Program preview link (story PP-3) - the two app-wide fences.
 *
 * 1. WRITE BLOCK. While a preview is open, every non-read request is refused at
 *    the transport: axios (REST), window.fetch (raw callers), and the Nexus Apollo
 *    link (mutations). Renderer's per-action wrapper is the polite layer; this is
 *    the one molecules with their own endpoints (S3 presign, VideoAsk, offer
 *    table, comments) cannot get around. GraphQL goes over POST, so the fetch
 *    fence hands /graphql to the Apollo link, which reads the operation kind.
 *
 * 2. TOKEN SCRUB. The preview URL carries a signed token. `no-referrer` stops the
 *    Referer header; it does nothing for Sentry / Datadog beacons, which carry the
 *    page URL in events, breadcrumbs and views. Deep-scrub every string before it
 *    leaves the browser.
 */
import { ApolloLink, Observable } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import type { DocumentNode } from 'graphql';
import type { AxiosRequestConfig } from 'axios';

/* The fence is up while EITHER the Guide's toggle is on OR any holder has acquired it.
   PP-8 mounts the Guide and the walkthrough under one entry that swaps them in place
   (`?view=guide`); a plain boolean let a child's "off" on unmount drop the fence while
   its sibling was mounting. */
let manualOn = false;
let holders = 0;
let notifyBlocked: (() => void) | null = null;
let lastNotified = 0;
const blockActive = (): boolean => manualOn || holders > 0;

export class PreviewBlockedError extends Error {
  readonly previewBlocked = true;

  constructor(what: string) {
    super(`Preview - not saved (${what} refused)`);
    this.name = 'PreviewBlockedError';
  }
}

/** ParticipantGuide flips this with `isPreview`; `notify` shows the snackbar. Turning it
 *  off never lowers a fence a holder still has acquired. */
export function setPreviewWriteBlock(on: boolean, notify?: () => void): void {
  manualOn = on;
  if (on) notifyBlocked = notify || null;
  else if (!holders) notifyBlocked = null;
}

/** Hold the fence for a lifetime (a route entry, say); returns the matching release.
 *  Releasing twice is a no-op. The fence stays up until the LAST holder releases and
 *  the manual toggle is off. */
export function acquirePreviewWriteBlock(notify?: () => void): () => void {
  holders += 1;
  if (notify) notifyBlocked = notify;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders = Math.max(0, holders - 1);
    if (!blockActive()) notifyBlocked = null;
  };
}

export const isPreviewWriteBlocked = (): boolean => blockActive();

/** For vendors with no beforeSend (Heap): do not boot them on a preview page load. */
export const isPreviewPath = (pathname: string): boolean =>
  pathname.startsWith('/program/preview/');

/** One toast per burst, however many requests a single click fans out into. */
export function notifyPreviewBlocked(): void {
  const now = Date.now();
  if (now - lastNotified < 1500) return;
  lastNotified = now;
  notifyBlocked?.();
}

const READ_METHODS = new Set(['get', 'head', 'options']);
export const isReadMethod = (method?: string | null): boolean =>
  READ_METHODS.has(String(method || 'get').toLowerCase());

export const isGraphqlUrl = (url: string): boolean => /\/graphql(\?|$)/.test(url);

export function isMutationDocument(doc: DocumentNode): boolean {
  try {
    const def = getMainDefinition(doc);
    return def.kind === 'OperationDefinition' && def.operation === 'mutation';
  } catch {
    return false;
  }
}

/** axios request interceptor: throws (so the caller's catch runs) on any write. */
export function previewAxiosInterceptor<T extends AxiosRequestConfig>(config: T): T {
  if (blockActive() && !isReadMethod(config.method)) {
    notifyPreviewBlocked();
    throw new PreviewBlockedError(`${String(config.method).toUpperCase()} ${config.url || ''}`);
  }
  return config;
}

/** Apollo context key a caller sets to send ONE mutation through a raised fence. Only for
 *  writes about the viewer's own visit (analytics), never about the doc on screen. */
export const PREVIEW_FENCE_EXEMPT = 'previewFenceExempt';

/** Apollo link for nexusClient: mutations error out, queries pass untouched. */
export const previewApolloLink = new ApolloLink((operation, forward) => {
  const exempt = operation.getContext()?.[PREVIEW_FENCE_EXEMPT] === true;
  if (blockActive() && !exempt && isMutationDocument(operation.query)) {
    notifyPreviewBlocked();
    return new Observable(observer => {
      observer.error(new PreviewBlockedError(`mutation ${operation.operationName || ''}`));
    });
  }
  return forward(operation);
});

/** Wraps window.fetch once. GraphQL is left to the Apollo link above. */
export function installPreviewFetchGuard(w: { fetch: typeof fetch } = window): void {
  const original = w.fetch.bind(w);
  // eslint-disable-next-line no-param-reassign -- replacing the global is the point
  w.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const req = typeof input === 'object' && 'url' in input ? input : null;
    const url = req ? req.url : String(input);
    const method = init?.method || req?.method || 'GET';
    if (blockActive() && !isReadMethod(method) && !isGraphqlUrl(url)) {
      notifyPreviewBlocked();
      return Promise.reject(new PreviewBlockedError(`${method.toUpperCase()} ${url}`));
    }
    return original(input, init);
  }) as typeof fetch;
}

// Raw, and percent-encoded as it appears inside another vendor's query string
// (a Datadog `resource.url` recording a Heap beacon carried `%2Fprogram%2Fpreview%2Fpp1...`).
const TOKEN_IN_PATH = /\/program\/preview\/[^\s/?#"'&]+/g;
const TOKEN_ENCODED = /%2Fprogram%2Fpreview%2F[^\s&"'%]+(?:%2[^F][^\s&"'%]*)*/gi;
export const TOKEN_PLACEHOLDER = '/program/preview/[token]';

/** Replace every preview token in every string of `value`, in place for objects. */
export function scrubPreviewTokens<T>(value: T, seen: WeakSet<object> = new WeakSet()): T {
  if (typeof value === 'string') {
    return value
      .replace(TOKEN_IN_PATH, TOKEN_PLACEHOLDER)
      .replace(TOKEN_ENCODED, encodeURIComponent(TOKEN_PLACEHOLDER)) as T;
  }
  if (!value || typeof value !== 'object' || seen.has(value as object)) return value;
  seen.add(value as object);
  const obj = value as Record<string, unknown>;
  Object.keys(obj).forEach(key => {
    obj[key] = scrubPreviewTokens(obj[key], seen);
  });
  return value;
}
